package br.com.texeratx.boardgametimeapp.ui.screens.clan

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.core.image.PickedImage
import br.com.texeratx.boardgametimeapp.core.session.SessionManager
import br.com.texeratx.boardgametimeapp.data.repository.ClanRepository
import br.com.texeratx.boardgametimeapp.domain.usecases.upload.UploadClanImageUseCase
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppSnackbarTone
import br.com.texeratx.boardgametimeapp.ui.feedback.AppUiEvent
import br.com.texeratx.boardgametimeapp.ui.feedback.AppUiEventBus
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class ClanViewModel(
    private val repo: ClanRepository,
    private val sessionManager: SessionManager,
    private val uploadClanImageUseCase: UploadClanImageUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    private val _state = MutableStateFlow(ClanUiState())
    val state = _state.asStateFlow()

    private var pendingImage: PickedImage? = null

    fun loadMe() {
        screenModelScope.launch {
            _state.value = _state.value.copy(isLoading = true, error = null)
            val session = sessionManager.load()
            val role = session?.activeRole.orEmpty().uppercase()
            val isOwner = role == "OWNER"
            val isAdmin = isOwner || role == "ADMIN"

            val result = repo.getActiveClan()
            result.fold(
                onSuccess = { dto ->
                    pendingImage = null
                    _state.value = _state.value.copy(
                        isLoading = false,
                        clan = dto,
                        nameInput = dto.name,
                        requireApprovalInput = dto.requireApproval,
                        isOwner = isOwner,
                        isAdmin = isAdmin,
                        localImageBytes = null,
                        localImageMimeType = null,
                        selectedImageName = null,
                        isUploadingImage = false,
                        error = null
                    )
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isLoading = false,
                        error = e.message ?: lang.strings.value.errorLoadingClan
                    )
                }
            )
        }
    }

    fun startEdit() {
        val c = _state.value.clan ?: return
        _state.value = _state.value.copy(
            isEditing = true,
            error = null,
            nameInput = c.name,
            requireApprovalInput = c.requireApproval
        )
    }

    fun cancelEdit() {
        val c = _state.value.clan
        pendingImage = null
        _state.value = _state.value.copy(
            isEditing = false,
            error = null,
            nameInput = c?.name ?: "",
            requireApprovalInput = c?.requireApproval ?: false,
            localImageBytes = null,
            localImageMimeType = null,
            selectedImageName = null,
            isUploadingImage = false
        )
    }

    fun onNameChange(value: String) {
        _state.value = _state.value.copy(nameInput = value)
    }

    fun onRequireApprovalChange(value: Boolean) {
        _state.value = _state.value.copy(requireApprovalInput = value)
    }

    fun onImageSelected(image: PickedImage) {
        pendingImage = image
        _state.value = _state.value.copy(
            selectedImageName = image.fileName,
            localImageBytes = image.bytes,
            localImageMimeType = image.mimeType
        )
    }

    fun clearLocalImage() {
        pendingImage = null
        _state.value = _state.value.copy(
            selectedImageName = null,
            localImageBytes = null,
            localImageMimeType = null
        )
    }

    fun openChangePasswordDialog() {
        _state.value = _state.value.copy(
            isChangePasswordDialogOpen = true,
            currentPasswordInput = "",
            newPasswordInput = "",
            confirmNewPasswordInput = "",
            changePasswordError = null
        )
    }

    fun closeChangePasswordDialog() {
        if (_state.value.isChangingPassword) return
        _state.value = _state.value.copy(
            isChangePasswordDialogOpen = false,
            currentPasswordInput = "",
            newPasswordInput = "",
            confirmNewPasswordInput = "",
            changePasswordError = null
        )
    }

    fun onCurrentPasswordChange(value: String) {
        _state.value = _state.value.copy(currentPasswordInput = value, changePasswordError = null)
    }

    fun onNewPasswordChange(value: String) {
        _state.value = _state.value.copy(newPasswordInput = value, changePasswordError = null)
    }

    fun onConfirmNewPasswordChange(value: String) {
        _state.value = _state.value.copy(confirmNewPasswordInput = value, changePasswordError = null)
    }

    fun submitPasswordChange() {
        val s = _state.value
        if (s.currentPasswordInput.isBlank()) {
            _state.value = s.copy(changePasswordError = lang.strings.value.passwordRequired)
            return
        }
        if (s.newPasswordInput.isBlank()) {
            _state.value = s.copy(changePasswordError = lang.strings.value.newPasswordRequired)
            return
        }
        if (s.currentPasswordInput == s.newPasswordInput) {
            _state.value = s.copy(changePasswordError = lang.strings.value.newPasswordMustBeDifferent)
            return
        }
        if (s.newPasswordInput != s.confirmNewPasswordInput) {
            _state.value = s.copy(changePasswordError = lang.strings.value.passwordsDoNotMatch)
            return
        }

        screenModelScope.launch {
            _state.value = _state.value.copy(isChangingPassword = true, changePasswordError = null)
            val result = repo.changePassword(s.currentPasswordInput, s.newPasswordInput)
            result.fold(
                onSuccess = {
                    _state.value = _state.value.copy(
                        isChangingPassword = false,
                        isChangePasswordDialogOpen = false,
                        currentPasswordInput = "",
                        newPasswordInput = "",
                        confirmNewPasswordInput = "",
                        changePasswordError = null
                    )
                    AppUiEventBus.publishMessage(
                        message = lang.strings.value.passwordChangedSuccess,
                        tone = AppSnackbarTone.Success
                    )
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isChangingPassword = false,
                        changePasswordError = e.message ?: lang.strings.value.unknownError
                    )
                }
            )
        }
    }

    fun save() {
        val name = _state.value.nameInput.trim()
        val requireApproval = _state.value.requireApprovalInput

        if (name.isBlank()) {
            _state.value = _state.value.copy(error = lang.strings.value.clanNameCannotBeEmpty)
            return
        }

        screenModelScope.launch {
            _state.value = _state.value.copy(isSaving = true, error = null)

            var finalImageUrl: String? = _state.value.clan?.imageUrl
            val pending = pendingImage

            if (pending != null) {
                _state.value = _state.value.copy(isUploadingImage = true)
                val uploadResult = uploadClanImageUseCase(
                    bytes = pending.bytes,
                    fileName = pending.fileName,
                    mimeType = pending.mimeType
                )
                uploadResult.fold(
                    onSuccess = { url ->
                        finalImageUrl = url
                        _state.value = _state.value.copy(isUploadingImage = false)
                    },
                    onFailure = { e ->
                        _state.value = _state.value.copy(
                            isUploadingImage = false,
                            isSaving = false,
                            error = e.message ?: lang.strings.value.errorUploadingImage
                        )
                        return@launch
                    }
                )
            }

            val result = repo.updateActiveClan(
                name = name,
                imageUrl = finalImageUrl,
                requireApproval = if (_state.value.isOwner) requireApproval else null
            )
            result.fold(
                onSuccess = { updated ->
                    pendingImage = null
                    _state.value = _state.value.copy(
                        isSaving = false,
                        isEditing = false,
                        clan = updated,
                        localImageBytes = null,
                        localImageMimeType = null,
                        selectedImageName = null,
                        nameInput = updated.name,
                        requireApprovalInput = updated.requireApproval,
                        error = null
                    )

                    runCatching {
                        val session = sessionManager.load() ?: return@runCatching
                        sessionManager.save(
                            session.copy(
                                activeClanName = updated.name,
                                activeClanImageUrl = updated.imageUrl
                            )
                        )
                    }

                    AppUiEventBus.publish(AppUiEvent.ClanUpdated)
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isSaving = false,
                        isUploadingImage = false,
                        error = e.message ?: lang.strings.value.errorSavingClan
                    )
                }
            )
        }
    }

    fun leaveClan(onDone: () -> Unit) {
        val session = runCatching { }.let { }
        val state = _state.value
        val clanId = state.clan?.id ?: return
        screenModelScope.launch {
            val current = sessionManager.load() ?: return@launch
            _state.value = state.copy(isLeaving = true, error = null)
            val r = repo.leaveClan(userId = current.userId, clanId = clanId)
            r.fold(
                onSuccess = {
                    sessionManager.clearActiveClan()
                    _state.value = _state.value.copy(isLeaving = false)
                    onDone()
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isLeaving = false,
                        error = e.message ?: lang.strings.value.unknownError
                    )
                }
            )
        }
    }
}
